import React, { useCallback, useEffect, useMemo } from 'react';
import NumberFormat from 'react-number-format';

import { useFeatureToggle } from '@flopflip/react-redux';
import { getBooleanField, getTextField } from '@lender/utils';
import { Box, Typography } from '@mui/material';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import { AutocompleteCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { object } from 'yup';

import { FeatureFlag } from 'constants/featureFlags';
import {
  DYNAMIC_FIELD_DEFAULT_MULTIPLIER,
  DynamicFieldPostfixSymbol,
  DynamicFieldPrefixSymbol,
  FormSchemaType,
} from 'constants/forms';
import {
  defaultDynamicFieldPostfixOption,
  defaultDynamicFieldPrefixOption,
  dynamicFieldPostfixOptions,
  dynamicFieldPrefixOptions,
} from 'constants/options';
import useForm from 'hooks/useForm';
import { getConditionalObject } from 'utils/data';
import {
  getActionProperty,
  getDecimalPositionsFromAction,
  getFieldDecimalPositionsDefault,
  isDecimalPositionsAllowed,
  isMultiplierAllowed,
  isPdfFormType,
} from 'utils/forms';

import { formsTestIds } from '../../forms.testIds';

import DynamicFieldBoldCheckbox from './DynamicFieldBoldCheckbox';
import { fullWidthCellStyles, sectionHeaderStyles } from './DynamicFieldOptions.styles';
import {
  findOptionByValue,
  getInitialHorizontalAlignment,
  getInitialVerticalAlignment,
} from './DynamicFieldOptions.utils';
import DynamicFieldOptionsLayout from './DynamicFieldOptionsLayout';
import StyleViewOptions from './StyleViewOptions';

const schema = object().shape({
  decimalPositions: getTextField({ isRequired: false }),
  isBold: getBooleanField({ isRequired: false }),
  isUppercase: getBooleanField({ isRequired: false }),
  fontSize: getTextField({ isRequired: false }),
  horizontalAlignment: getTextField({ isRequired: false }),
  verticalAlignment: getTextField({ isRequired: false }),
  fontColor: getTextField({ isRequired: false }),
  fontFamily: getTextField({ isRequired: false }),
});

const DynamicFieldRateCurrencyOptions = ({
  setParentView,
  chosenOption,
  handleClose,
  editor,
  onSave,
  action,
  style,
  dataDynamicPostfix,
  dataDynamicPrefix,
  dataDynamicMultiplier,
  isAvailablePostfix,
  isAvailablePrefix,
  schemaDefaultAction,
  isNewItem = false,
}) => {
  const [title, value, type, label] = chosenOption;
  const isPdfForm = isPdfFormType(editor.formType);
  const isBackgroundColorEnabled = useFeatureToggle(FeatureFlag.EnableDynamicFieldBackgroundColor);

  const isRate = type === FormSchemaType.RATE;
  const isCurrency = type === FormSchemaType.CURRENCY;
  const hasPercentPostfix = isAvailablePostfix || isRate || isCurrency;
  const hasDollarPrefix = isAvailablePrefix || isCurrency || isRate;
  const [includeSign] = action?.args || [];
  const initialDecimalPositions = getDecimalPositionsFromAction(action, type, schemaDefaultAction);

  const initialDisplayPercent =
    ([dataDynamicPrefix, includeSign].every(_.isUndefined) && isRate) ||
    dataDynamicPostfix === DynamicFieldPostfixSymbol.PERCENT ||
    (isRate && !!includeSign);
  const initialDisplayDollar =
    ([dataDynamicPrefix, includeSign].every(_.isUndefined) && isCurrency) ||
    dataDynamicPrefix === DynamicFieldPrefixSymbol.DOLLAR ||
    (isCurrency && !!includeSign);

  const initialPostfix = initialDisplayPercent
    ? findOptionByValue(dynamicFieldPostfixOptions, DynamicFieldPostfixSymbol.PERCENT)
    : defaultDynamicFieldPostfixOption;
  const initialPrefix = initialDisplayDollar
    ? findOptionByValue(dynamicFieldPrefixOptions, DynamicFieldPrefixSymbol.DOLLAR)
    : defaultDynamicFieldPrefixOption;

  const { form, setSchema, setInForm, validateField, handleChange } = useForm({
    postfix: hasPercentPostfix ? initialPostfix : undefined,
    prefix: hasDollarPrefix ? initialPrefix : undefined,
    includeSign: isRate ? undefined : includeSign,
    decimalPositions: initialDecimalPositions,
    isBold: !!style?.isBold,
    isUppercase: !!style?.isUppercase,
    horizontalAlignment: isPdfForm
      ? getInitialHorizontalAlignment({ align: style?.align, isNewItem })
      : undefined,
    verticalAlignment: isPdfForm
      ? getInitialVerticalAlignment({ verticalAlign: style?.verticalAlign })
      : undefined,
    multiplier: dataDynamicMultiplier ?? DYNAMIC_FIELD_DEFAULT_MULTIPLIER,
    fontColor: style?.fontColor,
    backgroundColor: isPdfForm ? style?.backgroundColor || null : null,
    fontSize: { value: style?.fontSize },
    fontFamily: { value: style?.fontFamily },
  });

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      const action = getActionProperty({
        ...form,
        dataDynamicType: type,
        includeSign: isRate || isCurrency ? false : form.includeSign,
        decimalPositions:
          form.decimalPositions === '' || _.isNil(form.decimalPositions)
            ? getFieldDecimalPositionsDefault(schemaDefaultAction, type)
            : Number(form.decimalPositions),
      });

      const dynamicPostfix = form.postfix?.value || undefined;
      const dynamicPrefix = form.prefix?.value || undefined;

      onSave({
        title,
        value,
        type,
        label,
        action,
        dataDynamicPostfix: dynamicPostfix,
        dataDynamicPrefix: dynamicPrefix,
        dataDynamicMultiplier:
          form.multiplier === '' || _.isNil(form.multiplier)
            ? DYNAMIC_FIELD_DEFAULT_MULTIPLIER
            : form.multiplier,
        style: {
          isBold: form.isBold,
          isUppercase: form.isUppercase,
          align: form.horizontalAlignment?.value,
          verticalAlign: form.verticalAlignment?.value,
          fontSize: form.fontSize?.value,
          fontColor: form.fontColor,
          fontFamily: form.fontFamily?.value,
          ...getConditionalObject(isPdfForm, { backgroundColor: form.backgroundColor || null }),
        },
      });
    },
    [onSave, title, value, type, label, form, isRate, isCurrency, isPdfForm, schemaDefaultAction],
  );

  const onAutocompleteChange = useMemo(
    () => onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onMultiplierBlur = useCallback(() => {
    if (form.multiplier !== '' && !_.isNil(form.multiplier)) {
      return;
    }

    setInForm('multiplier', DYNAMIC_FIELD_DEFAULT_MULTIPLIER);
  }, [form.multiplier, setInForm]);

  const onDecimalPositionsBlur = useCallback(() => {
    if (form.decimalPositions !== '' && !_.isNil(form.decimalPositions)) {
      return;
    }

    setInForm('decimalPositions', getFieldDecimalPositionsDefault(schemaDefaultAction, type));
  }, [form.decimalPositions, setInForm, type, schemaDefaultAction]);

  return (
    <DynamicFieldOptionsLayout
      chosenOption={chosenOption}
      dataTestId={formsTestIds.DYNAMIC_FIELD_RATE_CURRENCY_OPTIONS}
      editor={editor}
      setParentView={setParentView}
      onClose={handleClose}
      onSubmit={onSubmit}
    >
      <Box sx={fullWidthCellStyles}>
        <Typography color="text.secondary" sx={sectionHeaderStyles} variant="bold12">
          Display Options & Formatting
        </Typography>
      </Box>

      <DynamicFieldBoldCheckbox value={form.isBold} onChange={handleChange} />

      <Box />

      <AutocompleteCDS
        disableClearable
        dataTestId={formsTestIds.RATE_CURRENCY_PREFIX_SELECT}
        isHidden={!hasDollarPrefix}
        label="Prefix"
        name="prefix"
        options={dynamicFieldPrefixOptions}
        value={form.prefix}
        onChange={onAutocompleteChange}
      />

      <AutocompleteCDS
        disableClearable
        dataTestId={formsTestIds.RATE_CURRENCY_POSTFIX_SELECT}
        isHidden={!hasPercentPostfix}
        label="Postfix"
        name="postfix"
        options={dynamicFieldPostfixOptions}
        value={form.postfix}
        onChange={onAutocompleteChange}
      />

      <NumberFormat
        fullWidth
        allowNegative={false}
        customInput={TextFieldCDS}
        decimalScale={0}
        isAllowed={isDecimalPositionsAllowed}
        label="Decimal Positions"
        name="decimalPositions"
        placeholder={String(getFieldDecimalPositionsDefault(schemaDefaultAction, type))}
        type="text"
        value={form.decimalPositions}
        variant="outlined"
        onBlur={onDecimalPositionsBlur}
        onChange={handleChange}
      />

      <NumberFormat
        fullWidth
        allowNegative={false}
        customInput={TextFieldCDS}
        decimalScale={0}
        isAllowed={isMultiplierAllowed}
        label="Multiplier"
        name="multiplier"
        placeholder={String(DYNAMIC_FIELD_DEFAULT_MULTIPLIER)}
        type="text"
        value={form.multiplier}
        variant="outlined"
        onBlur={onMultiplierBlur}
        onChange={handleChange}
      />

      <StyleViewOptions
        backgroundColor={form.backgroundColor}
        fontColor={form.fontColor}
        fontFamily={form.fontFamily}
        fontSize={form.fontSize}
        handleChange={handleChange}
        horizontalAlignment={form.horizontalAlignment}
        isBackgroundColorEnabled={isPdfForm && isBackgroundColorEnabled}
        isBold={form.isBold}
        setInForm={setInForm}
        validateField={validateField}
        verticalAlignment={form.verticalAlignment}
      />
    </DynamicFieldOptionsLayout>
  );
};

DynamicFieldRateCurrencyOptions.propTypes = {
  isAvailablePostfix: PropTypes.bool.isRequired,
  isAvailablePrefix: PropTypes.bool.isRequired,
  chosenOption: PropTypes.array.isRequired,
  editor: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  setParentView: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  action: PropTypes.object,
  dataDynamicMultiplier: PropTypes.number,
  dataDynamicPostfix: PropTypes.string,
  dataDynamicPrefix: PropTypes.string,
  schemaDefaultAction: PropTypes.object,
  style: PropTypes.object,
  isNewItem: PropTypes.bool,
};

export default DynamicFieldRateCurrencyOptions;
